import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ShoppingCart, User, Menu, X, Sun, Search, LogOut, Settings, Sparkles, Store, Flame, MapPin, Package, Heart, BookOpen } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Store,
  Sparkles,
  Flame,
  MapPin,
  Sun,
  Package,
  Heart,
  BookOpen
};

const DEFAULT_NAV_LINKS = [
  { id: 'store', name: 'Divine Store', href: '/store', icon: 'Store' },
  { id: 'pujas', name: 'Online Pujas', href: '/pujas', icon: 'Sparkles' },
  { id: 'offerings', name: 'Offerings', href: '/offerings', icon: 'Flame' },
  { id: 'pilgrimage', name: 'Pilgrimage', href: '/travel', icon: 'MapPin' },
];

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState(DEFAULT_NAV_LINKS);
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;
    axios.get(`${API}/settings/menu`)
      .then(res => {
        if (isMounted && res.data?.menu_items && Array.isArray(res.data.menu_items) && res.data.menu_items.length > 0) {
          const activeItems = res.data.menu_items
            .filter(item => item.is_active !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          if (activeItems.length > 0) {
            setNavItems(activeItems);
          }
        }
      })
      .catch(() => {
        // Fallback to DEFAULT_NAV_LINKS
      });
    return () => { isMounted = false; };
  }, []);

  const getIconComponent = (iconName) => {
    if (typeof iconName === 'object' && iconName !== null) return iconName;
    return ICON_MAP[iconName] || Sparkles;
  };

  const isLinkActive = (href) => {
    if (href === '/store') {
      return location.pathname.startsWith('/store') || location.pathname.startsWith('/product');
    }
    if (href === '/pujas' || href === '/puja' || href === '/temples') {
      return location.pathname.startsWith('/puja') || location.pathname.startsWith('/temple');
    }
    if (href === '/offerings') {
      return location.pathname.startsWith('/offerings') || location.pathname.startsWith('/offering');
    }
    if (href === '/travel') {
      return location.pathname.startsWith('/travel');
    }
    return location.pathname === href;
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-[#FBF4E7]/95 backdrop-blur-md border-b border-[#E7D9BC] shadow-xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#4E1225] hover:text-[#D9711F] transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <Link to="/" className="flex items-center space-x-3 group" data-testid="logo-link">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 2 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF7B00] to-[#D4AF37] flex items-center justify-center shadow-md group-hover:shadow-lg transition-all relative overflow-hidden flex-shrink-0"
              >
                <div className="absolute inset-0 bg-white/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <span className="text-white font-heading font-bold text-lg relative z-10">प</span>
              </motion.div>
              <div className="flex flex-col leading-tight">
                <span className="font-serif-heading text-xl font-bold text-[#4E1225] tracking-tight group-hover:text-[#D9711F] transition-colors">
                  Paramparik
                </span>
                <span className="text-[9.5px] font-semibold tracking-[1.2px] uppercase text-[#83725B] -mt-0.5">
                  Authentic Puja Essentials
                </span>
              </div>
            </Link>
          </div>

          {/* Center: Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((link) => {
              const active = isLinkActive(link.href);
              return (
                <Link
                  key={link.id || link.name}
                  to={link.href}
                  className={`text-sm font-medium transition-colors py-1 ${
                    active ? 'text-[#B85A15] font-bold border-b-2 border-[#B85A15]' : 'text-[#2A1F16] hover:text-[#B85A15]'
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </nav>

          {/* Right: Actions (Search, Cart, User) */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/store')}
              className="p-2 text-[#4E1225] hover:text-[#D9711F] transition-colors rounded-full hover:bg-[#F3E7D0]"
              aria-label="Search Store"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Cart Link */}
            <Link
              to="/cart"
              className="relative p-2 text-[#4E1225] hover:text-[#D9711F] transition-colors rounded-full hover:bg-[#F3E7D0]"
              aria-label="Shopping Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D9711F] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Account Menu (Desktop / Tablet) */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-1.5 rounded-full bg-[#F3E7D0] border border-[#E7D9BC] hover:bg-[#E8D5A8] transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-[#6E1B34] text-[#E8D5A8] flex items-center justify-center font-bold text-xs">
                    {user.name?.charAt(0) || 'U'}
                  </div>
                  <span className="hidden sm:inline text-xs font-semibold text-[#2A1F16] pr-1">
                    {user.name?.split(' ')[0]}
                  </span>
                </button>

                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-[#E7D9BC] py-2 z-50"
                    >
                      <div className="px-4 py-2 border-b border-[#E7D9BC]">
                        <p className="text-xs font-bold text-[#2A1F16]">{user.name}</p>
                        <p className="text-[11px] text-[#83725B] truncate">{user.email}</p>
                      </div>

                      <Link
                        to="/dashboard"
                        className="flex items-center space-x-2.5 px-4 py-2 text-xs font-semibold text-[#2A1F16] hover:bg-[#FBF4E7] hover:text-[#6E1B34]"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4 text-[#83725B]" />
                        <span>My Dashboard</span>
                      </Link>

                      {user.role === 'admin' && (
                        <Link
                          to="/admin"
                          className="flex items-center space-x-2.5 px-4 py-2 text-xs font-semibold text-[#D9711F] hover:bg-[#FBF4E7]"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4" />
                          <span>Admin Panel</span>
                        </Link>
                      )}

                      <button
                        onClick={() => { logout(); setIsUserMenuOpen(false); }}
                        className="flex items-center space-x-2.5 w-full text-left px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to="/login" className="hidden sm:inline-block">
                <button className="px-4 py-1.5 rounded-md bg-[#6E1B34] text-[#FFFFFF] hover:bg-[#4E1225] font-semibold text-xs transition-colors shadow-sm">
                  Login
                </button>
              </Link>
            )}

          </div>
        </div>
      </div>

      {/* Quick Action Navigation Bar on Mobile View - Sticky right below Header */}
      <div className="lg:hidden border-t border-[#E7D9BC]/80 bg-[#FBF4E7]/98 backdrop-blur-md px-2 py-1.5 sm:px-4 sm:py-2 shadow-2xs">
        <div className={`grid ${navItems.length >= 4 ? 'grid-cols-4' : 'grid-cols-3'} gap-1 sm:gap-2`}>
          {navItems.slice(0, 4).map((link) => {
            const active = isLinkActive(link.href);
            const IconComp = getIconComponent(link.icon);
            return (
              <Link
                key={link.id || link.name}
                to={link.href}
                className={`flex items-center justify-center gap-1 sm:gap-1.5 py-2 px-1 sm:px-2 rounded-xl text-[10px] sm:text-xs font-bold transition-all border text-center shadow-2xs active:scale-95 ${
                  active
                    ? 'bg-[#6E1B34] text-white border-[#6E1B34] shadow-xs'
                    : 'bg-white/95 text-[#4E1225] border-[#E7D9BC] hover:bg-[#F3E7D0] hover:border-[#D9711F]/50'
                }`}
                data-testid={`mobile-quick-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <IconComp className={`w-3.5 h-3.5 flex-shrink-0 ${active ? 'text-[#E8D5A8]' : 'text-[#D9711F]'}`} />
                <span className="truncate">{link.name}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-[#E7D9BC] bg-[#FBF4E7] px-4 py-3 space-y-2"
          >
            {navItems.map((link) => {
              const IconComp = getIconComponent(link.icon);
              return (
                <Link
                  key={link.id || link.name}
                  to={link.href}
                  className="flex items-center space-x-3 p-3 rounded-lg text-[#2A1F16] font-medium hover:bg-[#F3E7D0] hover:text-[#6E1B34]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <IconComp className="w-5 h-5 text-[#83725B]" />
                  <span>{link.name}</span>
                </Link>
              );
            })}

            {/* Mobile Account / Login Option inside Menu */}
            <div className="pt-2 mt-2 border-t border-[#E7D9BC]">
              {user ? (
                <div className="space-y-1">
                  <div className="px-3 py-1.5 text-xs font-bold text-[#6E1B34]">
                    Hello, {user.name}
                  </div>
                  <Link
                    to="/dashboard"
                    className="flex items-center space-x-3 p-2.5 rounded-lg text-[#2A1F16] font-medium hover:bg-[#F3E7D0]"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <User className="w-4 h-4 text-[#83725B]" />
                    <span>My Dashboard</span>
                  </Link>
                  {user.role === 'admin' && (
                    <Link
                      to="/admin"
                      className="flex items-center space-x-3 p-2.5 rounded-lg text-[#D9711F] font-medium hover:bg-[#F3E7D0]"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4" />
                      <span>Admin Panel</span>
                    </Link>
                  )}
                  <button
                    onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                    className="flex items-center space-x-3 w-full p-2.5 rounded-lg text-red-600 font-medium hover:bg-red-50 text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center justify-center space-x-2 p-3 rounded-xl bg-[#6E1B34] text-white font-bold text-sm shadow-md mt-1"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <User className="w-4 h-4" />
                  <span>Login / Account</span>
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
